import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Briefcase,
  Users,
  Calendar,
  TrendingUp,
  ArrowRight,
  Clock,
} from 'lucide-react'
import { dashboardApi, jobsApi, api } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  StatCard,
  PipelineFunnelChart,
  SourceEffectivenessChart,
  TimeToHireChart,
  ApplicationsOverTimeChart,
} from '@/components/features/analytics/AnalyticsCharts'

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
  })

  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', { status: 'published', limit: '5' }],
    queryFn: () => jobsApi.list({ status: 'published', limit: '5' }),
  })

  const { data: pipelineApiData } = useQuery({
    queryKey: ['reports-pipeline'],
    queryFn: () => api.get<any>('/reports/pipeline'),
  })

  const { data: sourceApiData } = useQuery({
    queryKey: ['reports-sources'],
    queryFn: () => api.get<any>('/reports/sources'),
  })

  const { data: timeToHireApiData } = useQuery({
    queryKey: ['reports-time-to-hire'],
    queryFn: () => api.get<any>('/reports/time-to-hire'),
  })

  const { data: applicationsOverTimeApiData } = useQuery({
    queryKey: ['reports-applications-over-time'],
    queryFn: () => api.get<any[]>('/reports/applications-over-time'),
  })

  const statsData = stats as any || {}

  const stageColors: Record<string, string> = {
    applied: '#6b7280',
    screening: '#3b82f6',
    interview: '#8b5cf6',
    offer: '#f59e0b',
    hired: '#10b981',
  }

  const pipelineData = (pipelineApiData?.stages || []).map((s: any) => ({
    stage: s.name,
    count: s.count,
    color: stageColors[s.type] || '#6b7280',
  }))

  const sourceData = (sourceApiData?.sources || []).map((s: any) => ({
    source: s.source,
    applications: s.applications,
    hires: s.hired || 0,
    conversionRate: s.conversionRate || 0,
  }))

  const timeToHireData = (timeToHireApiData?.monthly || []).map((m: any) => ({
    month: m.month,
    days: m.days,
  }))

  const applicationsOverTime = (Array.isArray(applicationsOverTimeApiData) ? applicationsOverTimeApiData : []).map((m: any) => ({
    date: m.month || m.date,
    count: m.count,
  }))

  const statsToolbar = (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
      >
        <StatCard
          title="Active Jobs"
          value={statsLoading ? '...' : statsData.activeJobs || 0}
          icon={Briefcase}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, delay: 0.03 }}
      >
        <StatCard
          title="Total Applications"
          value={statsLoading ? '...' : statsData.totalApplications || 0}
          icon={Users}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, delay: 0.06 }}
      >
        <StatCard
          title="Interviews"
          value={statsLoading ? '...' : statsData.scheduledInterviews || 0}
          icon={Calendar}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, delay: 0.09 }}
      >
        <StatCard
          title="Avg Time to Hire"
          value={statsLoading ? '...' : statsData.avgTimeToHire ? `${statsData.avgTimeToHire}d` : 'N/A'}
          icon={Clock}
        />
      </motion.div>
    </div>
  )

  return (
    <PageLayout fullHeight toolbar={statsToolbar}>
      {/* Bento Grid - fills remaining viewport on desktop */}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-6 lg:grid-rows-2 h-full">
        {/* Applications Over Time - top left */}
        <motion.div
          className="lg:col-span-3 min-h-[200px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.1 }}
        >
          <ApplicationsOverTimeChart data={applicationsOverTime} />
        </motion.div>

        {/* Pipeline Funnel - top right */}
        <motion.div
          className="lg:col-span-3 min-h-[200px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.12 }}
        >
          <PipelineFunnelChart data={pipelineData} />
        </motion.div>

        {/* Source Effectiveness - bottom left */}
        <motion.div
          className="lg:col-span-2 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.14 }}
        >
          <SourceEffectivenessChart data={sourceData} />
        </motion.div>

        {/* Time to Hire - bottom center */}
        <motion.div
          className="lg:col-span-1 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.15 }}
        >
          <TimeToHireChart data={timeToHireData} />
        </motion.div>

        {/* Active Jobs - bottom right */}
        <motion.div
          className="lg:col-span-3 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.15 }}
        >
          <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between flex-shrink-0 py-3 px-4">
              <CardTitle className="text-sm">Active Jobs</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/jobs">
                  View all
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-y-auto px-4 pb-3 pt-0">
              {jobsLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-12 skeleton rounded-md" />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {((jobsData as any)?.items || []).slice(0, 5).map((job: any) => (
                    <Link
                      key={job.id}
                      to="/jobs/$jobId"
                      params={{ jobId: job.id }}
                      className="flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{job.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {job.department?.name || 'No department'} &middot;{' '}
                          {job.location?.name || 'Remote'}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs ml-2 flex-shrink-0">
                        {job.applicationCount || 0}
                      </Badge>
                    </Link>
                  ))}
                  {((jobsData as any)?.items || []).length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      <Briefcase className="h-6 w-6 mx-auto mb-1 opacity-50" />
                      <p className="text-sm">No active jobs yet</p>
                      <Button variant="link" size="sm" asChild className="mt-1">
                        <Link to="/jobs">Create your first job</Link>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions - mobile only */}
      <div className="lg:hidden">
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 px-4 pb-3 pt-0">
            <Button variant="outline" className="justify-start h-auto py-2" asChild>
              <Link to="/jobs/new">
                <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Post a new job</span>
              </Link>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-2" asChild>
              <Link to="/candidates">
                <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Add a candidate</span>
              </Link>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-2" asChild>
              <Link to="/interviews">
                <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                <span className="text-sm font-medium">Schedule an interview</span>
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}
